import { useContext, useEffect, useState, useRef } from "react";
import api from "../api/axios";

import { NavLink } from "react-router-dom";

import {
  Divider,
  Grid,
  Typography,
  Button,
  Chip,
  Box,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import {
  Markunread,
  BusinessCenter as BusinessCenterIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime,
  Gavel,
  Warning,
} from "@mui/icons-material";

import "./Scss/PanelStyles.scss?v=1.1.5";
import { UserContext } from "../Components/Context/Context";
import {
  dateFormat,
  formatToTenDigits,
  quitarTildes,
} from "../Components/helpers/Helpers";
import dayjs from "dayjs";
import { CustomIconsPlusText } from "../Components/Common/Design/CustomIconsPlusText";
import LoadingPage from "../Components/Common/Loading/LoadingPage";

const PAGE_SIZE = 20;

const Panel = () => {
  const { userData } = useContext(UserContext);
  const { user } = userData;

  const [pendingAnotherCases, setPendingAnotherCases] = useState([]);
  const [allUserCases, setAllUserCases] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);
  const [newContacts, setNewContacts] = useState([]);
  const [allUserCasesMessages, setAllUserCasesMessages] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [flag, setFlag] = useState(false);

  // Lazy load refs
  const contactsRef = useRef();
  const alertsRef = useRef();
  const pendingRef = useRef();
  const anotherRef = useRef();

  const [contactsVisible, setContactsVisible] = useState(PAGE_SIZE);
  const [alertsVisible, setAlertsVisible] = useState(PAGE_SIZE);
  const [pendingVisible, setPendingVisible] = useState(PAGE_SIZE);
  const [anotherVisible, setAnotherVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!user?.id) return;

    const getDataPanel = async () => {
      setFlag(false);
      try {
        const { data } = await api.post("/infopanel", { user_id: user.id });

        console.log(data);

        // no section, devuelve todo
        const {
          contacts = [],
          alerts = [],
          anotherCases = [],
          pendingCases = [],
          allUserCases = [],
          allUserCasesMessages = [],
        } = data || {};

        setAllUserCasesMessages(allUserCasesMessages);
        setAllUserCases(allUserCases);
        setPendingCases(pendingCases);
        setPendingAnotherCases(anotherCases);
        setNewContacts(contacts);
        setAlerts(alerts);
      } catch (error) {
        console.error("Error al cargar datos del panel:", error);
        setAllUserCasesMessages([]);
        setAllUserCases([]);
        setPendingCases([]);
        setPendingAnotherCases([]);
        setAlerts([]);
        setNewContacts([]);
      } finally {
        setFlag(true);
      }
    };

    getDataPanel();
  }, [user]);

  /* ===========================
     Funciones comunes
  ============================ */

  /* ===========================
   Lazy Load Functions
=========================== */
  const fetchSection = async (section, offset = 0, limit = PAGE_SIZE) => {
    if (!user?.id) return;
    try {
      const { data } = await api.post("/infolazy", {
        user_id: user.id,
        section, // 'contacts', 'alerts', ...
        offset,
        limit,
      });

      if (!data.success) return;

      switch (section) {
        case "contacts":
          if (offset === 0) setNewContacts(data.data || []);
          else setNewContacts((prev) => [...prev, ...(data.data || [])]);
          break;
        case "alerts":
          if (offset === 0) setAlerts(data.data || []);
          else setAlerts((prev) => [...prev, ...(data.data || [])]);
          break;
        case "pendingCases":
          if (offset === 0) setPendingCases(data.data || []);
          else setPendingCases((prev) => [...prev, ...(data.data || [])]);
          break;
        case "anotherCases":
          if (offset === 0) setPendingAnotherCases(data.data || []);
          else
            setPendingAnotherCases((prev) => [...prev, ...(data.data || [])]);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Error cargando ${section}:`, err);
    }
  };

  const RenderVencimiento = ({
    last_task: current_task,
    cases_flow_relation: flow,
    flagFromPanel = false,
  }) => {
    if (!current_task?.date_end) return null;

    const fechaFormateada = dateFormat(current_task.date_end);
    const iconSize = { width: "15px", height: "15px" };
    const diffDays = dayjs(current_task.date_end).diff(dayjs(), "day");
    const isJuicio = quitarTildes(flow?.status_name) === "juicio";
    const isActive = current_task?.status === 0;

    const renderEstado = () => {
      if (!isActive) return null;
      if (isJuicio) {
        if (diffDays > 15)
          return (
            <CustomIconsPlusText
              icon={Gavel}
              iconStyles={iconSize}
              text="FECHA DE JUICIO"
              blink="blinking"
              severity="warning"
              variant="contained"
            />
          );
        if (diffDays > 0 && diffDays <= 15)
          return (
            <CustomIconsPlusText
              icon={Gavel}
              iconStyles={iconSize}
              text={`JUICIO EN ${diffDays} DÍAS`}
              blink="blinking"
              severity="error"
              variant="contained"
            />
          );
      }
      if (diffDays > 5)
        return (
          <CustomIconsPlusText
            icon={AccessTime}
            iconStyles={iconSize}
            severity="info"
            text={`${diffDays} días para finalizar la tarea`}
            variant="outlined"
          />
        );
      if (diffDays > 0 && diffDays <= 5)
        return (
          <CustomIconsPlusText
            icon={Warning}
            iconStyles={iconSize}
            blink="blinking"
            severity="warning"
            text={`${diffDays} días para finalizar la tarea`}
            variant="outlined"
          />
        );
      return (
        <CustomIconsPlusText
          icon={Warning}
          iconStyles={iconSize}
          blink="blinking"
          severity="error"
          text={`Tarea vencida hace ${Math.abs(diffDays)} días`}
          variant="outlined"
        />
      );
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: flagFromPanel ? "row" : "column",
          gap: "3px",
          padding: "2px",
        }}>
        {renderEstado()}
        <CustomIconsPlusText
          icon={AccessTime}
          iconStyles={iconSize}
          severity="none"
          text={`Fecha: ${fechaFormateada}`}
          variant="outlined"
        />
      </div>
    );
  };

  /* ===========================
     Lazy load scroll
  ============================ */

  const handleScrollLazy = (ref, setVisible, visible, fetchFn) => {
    if (!ref.current) return;
    const el = ref.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      fetchFn(visible, PAGE_SIZE);
      setVisible((prev) => prev + PAGE_SIZE);
    }
  };

  /* ===========================
     Renderers por sección
  ============================ */

  const renderNewContacts = () => {
    if (!newContacts.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No tienes contactos nuevos pendientes.
        </Typography>
      );

    return (
      <Box
        ref={contactsRef}
        onScroll={() =>
          handleScrollLazy(
            contactsRef,
            setContactsVisible,
            contactsVisible,
            () => fetchSection("contacts", contactsVisible, PAGE_SIZE),
          )
        }
        sx={{
          maxHeight: "380px", // máximo permitido
          minHeight: "50px", // ajustable al contenido
          overflow: "auto",
        }}>
        {newContacts.slice(0, contactsVisible).map((c, idx) => {
          const daysAgo = c.date_in
            ? dayjs().diff(dayjs(c.date_in), "day")
            : null;
          let chipColor = "#23c6c8";
          if (daysAgo !== null) {
            if (daysAgo > 10) chipColor = "#e74c3c";
            else if (daysAgo > 5) chipColor = "#f39c12";
          }
          return (
            <Box
              key={idx}
              sx={{
                p: 1,
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <Typography fontWeight={600}>
                {c.name} {c.surname}
              </Typography>
              <Box>
                <Chip
                  label={
                    daysAgo !== null
                      ? `Creado hace ${daysAgo} día${daysAgo !== 1 ? "s" : ""}`
                      : "Fecha no disponible"
                  }
                  size="small"
                  sx={{ backgroundColor: chipColor, color: "white", mr: 2 }}
                />
                <Button
                  color="info"
                  variant="outlined"
                  size="small"
                  component={NavLink}
                  to={`/contactos/ver?id=${c.user_id}`}>
                  Ir a contacto
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAlerts = () => {
    if (!alerts.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No tienes alertas activas.
        </Typography>
      );
    return (
      <Box
        ref={alertsRef}
        onScroll={() =>
          handleScrollLazy(alertsRef, setAlertsVisible, alertsVisible, () =>
            fetchSection("alerts", alertsVisible, PAGE_SIZE),
          )
        }
        sx={{
          maxHeight: "380px", // límite máximo del scroll
          minHeight: "50px", // se ajusta al contenido mínimo
          overflow: "auto",
        }}>
        {alerts
          .slice(0, alertsVisible)
          .map(({ case_id, date, message }, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                p: 1,
                borderRadius: "6px",
                background: "#f9f9f9",
                border: "1px solid #eee",
              }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}>
                <Chip label={`#${formatToTenDigits(case_id)}`} size="small" />
                <Chip
                  label={date}
                  size="small"
                  sx={{ backgroundColor: "#23c6c8", color: "white" }}
                />
                <Typography
                  variant="body2"
                  className="myMessage"
                  dangerouslySetInnerHTML={{ __html: message }}
                />
              </Box>
              <Button
                size="small"
                component={NavLink}
                to={`/expedientes/ver?id=${case_id}`}
                variant="outlined"
                color="success">
                Ir al Expediente
              </Button>
            </Box>
          ))}
      </Box>
    );
  };

  const renderPendingNotification = () => {
    if (!pendingCases.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No hay cambios recientes en tus expedientes.
        </Typography>
      );
    const sorted = [...pendingCases].sort((a, b) => {
      const dateA = a?.last_task?.date_end ? dayjs(a.last_task.date_end) : null;
      const dateB = b?.last_task?.date_end ? dayjs(b.last_task.date_end) : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.diff(dateB);
    });
    return (
      <Box
        ref={pendingRef}
        onScroll={() =>
          handleScrollLazy(pendingRef, setPendingVisible, pendingVisible, () =>
            fetchSection("pendingCases", pendingVisible, PAGE_SIZE),
          )
        }
        sx={{
          maxHeight: "380px", // límite máximo del scroll
          minHeight: "50px", // se ajusta al contenido mínimo
          overflow: "auto",
        }}>
        {sorted.slice(0, pendingVisible).map((item, idx) => (
          <Box
            key={idx}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 1.5,
              backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Expediente #{formatToTenDigits(item.id)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.full_case_status_name}
                </Typography>
              </Box>
              <Button
                size="small"
                component={NavLink}
                to={`/expedientes/ver?id=${item.id}`}
                variant="outlined"
                color="success">
                Ir al expediente
              </Button>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <RenderVencimiento {...item} flagFromPanel={true} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderAnotherNotifications = () => {
    if (!pendingAnotherCases.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No hay cambios recientes en tus expedientes.
        </Typography>
      );
    const sorted = [...pendingAnotherCases].sort((a, b) => {
      const dateA = a?.last_task?.date_end ? dayjs(a.last_task.date_end) : null;
      const dateB = b?.last_task?.date_end ? dayjs(b.last_task.date_end) : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.diff(dateB);
    });
    return (
      <Box
        ref={anotherRef}
        onScroll={() =>
          handleScrollLazy(anotherRef, setAnotherVisible, anotherVisible, () =>
            fetchSection("anotherCases", anotherVisible, PAGE_SIZE),
          )
        }
        sx={{
          height: anotherVisible < sorted.length ? "300px" : "auto",
          overflow: "auto",
        }}>
        {sorted.slice(0, anotherVisible).map((item, idx) => (
          <Box
            key={idx}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 1.5,
              backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Expediente #{formatToTenDigits(item.id)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.full_case_status_name}
                </Typography>
              </Box>
              <Button
                size="small"
                component={NavLink}
                to={`/expedientes/ver?id=${item.id}`}
                variant="outlined"
                color="success">
                Ir al expediente
              </Button>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <RenderVencimiento {...item} flagFromPanel={true} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const getStatusStyles = (statusName = "") => {
    const normalized = quitarTildes(statusName.toLowerCase());
    if (normalized.includes("cancelad")) return { borderColor: "#d32f2f" };
    if (normalized.includes("pendiente")) return { borderColor: "#f9a825" };
    return { borderColor: "#1976d2" };
  };

  const summarizedCases = allUserCases.reduce((acc, item) => {
    const taskStatus = item?.cases_tasks_individual_relation?.status;
    if (taskStatus === 0 || taskStatus === undefined) return acc;
    const statusName =
      item?.full_case_status_name ||
      item?.cases_flow_relation?.status_name ||
      "";
    const excludedStateKeywords = ["pendiente", "finalizado"];
    const normalized = quitarTildes(statusName.toLowerCase());
    if (excludedStateKeywords.some((word) => normalized.includes(word)))
      return acc;
    const caseTypeName = item?.case_type_name || "Sin tipo";
    if (!acc[statusName]) acc[statusName] = {};
    if (!acc[statusName][caseTypeName]) acc[statusName][caseTypeName] = 0;
    acc[statusName][caseTypeName] += 1;
    return acc;
  }, {});

  const renderCasesSummary = () => {
    const statusEntries = Object.entries(summarizedCases);
    if (!statusEntries.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No tienes expedientes en estados avanzados.
        </Typography>
      );
    return (
      <Grid container spacing={2}>
        {statusEntries.map(([statusName, caseTypes], idx) => {
          const styles = getStatusStyles(statusName);
          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  borderLeft: `4px solid ${styles.borderColor}`,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}>
                  Estado
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {statusName}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {Object.entries(caseTypes).map(([caseTypeName, count], i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">{caseTypeName}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const infoSections = [
    {
      title: "Nuevos contactos",
      count: newContacts.length,
      renderContent: renderNewContacts,
    },
    { title: "Tus Alertas", count: alerts.length, renderContent: renderAlerts },
    {
      title: "Tus Notificaciones",
      count: pendingCases.length,
      renderContent: renderPendingNotification,
    },
    {
      title: "Otras Notificaciones",
      count: pendingAnotherCases.length,
      renderContent: renderAnotherNotifications,
    },
  ];

  const panelStats = [
    {
      color: "#23c5c8",
      icon: <Markunread className="panelIcon" />,
      label: "Nuevos emails",
      value: allUserCasesMessages?.length,
    },
    {
      color: "#f8ac59",
      icon: <BusinessCenterIcon className="panelIcon" />,
      label: "Tus expedientes",
      value: allUserCases?.length,
    },
  ];

  return (
    <>
      {!flag && <LoadingPage isLoading={true} />}
      <Fade in={flag} timeout={600}>
        <Grid container item xs={11} className="panelContainer" m="20px auto">
          <Grid item xs={12} mb={1}>
            <Typography variant="h4">
              Bienvenid@, {user?.name} {user?.surname}
            </Typography>
          </Grid>
          <Grid item xs={12} mb={1}>
            <Typography variant="body1">
              Tienes {allUserCasesMessages.length} mensajes sin leer,{" "}
              {pendingCases.length} notificaciones y {alerts.length} alertas
            </Typography>
          </Grid>
          <Divider sx={{ width: "100%", mb: 2 }} />
          <Grid item xs={12}>
            {infoSections.map(({ title, count, renderContent }, index) => (
              <Accordion
                key={index}
                defaultExpanded={index === 0}
                sx={{
                  mb: 1,
                  borderRadius: "6px",
                  "&:before": { display: "none" },
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}>
                    <Typography fontWeight={600}>{title}</Typography>
                    <Chip
                      label={count}
                      size="small"
                      sx={{ backgroundColor: "#1976d2", color: "white" }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>{renderContent()}</AccordionDetails>
              </Accordion>
            ))}
          </Grid>
          {/* Stats panel */}
          <Grid container justifyContent="center" height={130} mt={2}>
            {panelStats.map(({ color, icon, label, value }, index) => (
              <Grid key={index} item xs={6} p={0.5} sx={{ height: "100%" }}>
                <Grid
                  container
                  sx={{
                    background: `linear-gradient(135deg, ${color} 0%, #ffffffff 100%)`,
                    borderRadius: 1,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    height: "100%",
                    color: "white",
                  }}>
                  <Grid
                    item
                    xs={4}
                    sx={{ display: "flex", alignItems: "center", p: 1 }}>
                    {icon}
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    container
                    direction="column"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    p={1}>
                    <Typography sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "64px",
                        lineHeight: 1,
                        fontWeight: 400,
                        textShadow: "4px 2px 4px gray",
                      }}>
                      {value}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12} mt={3}>
            <Typography variant="h6" className="title">
              Tus Expedientes
            </Typography>
            <Grid item xs={12} mt={1}>
              {renderCasesSummary()}
            </Grid>
          </Grid>
        </Grid>
      </Fade>
    </>
  );
};

export default Panel;
